import React, {
  // Dispatch, SetStateAction,
  useState,
  useEffect,
  // useCallback,
  // useMemo,
} from 'react';
import shopifyClient from '../lib/shopifyClient';

import debug from 'debug';
import { Cart, CustomAttribute } from 'shopify-buy';
import useNotification from 'effects/useNotification';
import { getShopifyProxySiteSearchResults, getShopifyProxyProductTags } from 'lib/shopifyproxy';
import { cgtag } from 'utilities/gtag';
import get from 'lodash/get';
import { shopifyLineItemToEcommerceLineItem } from 'lib/shopifyHelpers';

const d = debug('shopifyContext');
d('contextInitialized');

interface CGGiftCardBalance {
  amount: string;
}

interface CGGiftCard {
  id: string;
  lastCharacters: string;
  balanceV2: CGGiftCardBalance;
}

interface CGCart extends Cart {
  appliedGiftCards?: CGGiftCard[];
  webUrl?: string;
  currencyCode?: string;
}

interface CGLineItem {
  variantId: string;
  quantity: number;
  customAttributes: CustomAttribute[];
}

interface IShopifyContextInterface {
  checkoutInitialized: boolean;
  checkoutBusy: boolean;
  checkoutState: CGCart | undefined;
  lastCheckoutState: CGCart | undefined;
  addLineItems: (lineItemsToAdd: CGLineItem[]) => Promise<any>;
  addGiftCards: (code: string) => Promise<any>;
  applyGiftCard: (code: string) => void;
  removeGiftCard: (id: string) => Promise<any>;
  removeLineItem: (lineItemId: string) => Promise<any>;
  updateLineItem: (lineItemId: string, quantity: number, cardGiftData: string) => Promise<any>;
  predictiveSearch: (query: string) => any;
  getProductTags: () => Promise<any>;
}

export const shopifyContext = React.createContext<IShopifyContextInterface>({
  checkoutInitialized: false,
  checkoutBusy: false,
  checkoutState: undefined,
  lastCheckoutState: undefined,
  addLineItems: () => Promise.resolve(),
  addGiftCards: () => Promise.resolve(),
  removeGiftCard: () => Promise.resolve(),
  removeLineItem: () => Promise.resolve(),
  updateLineItem: () => Promise.resolve(),
  applyGiftCard: () => {},
  predictiveSearch: () => Promise.resolve(),
  getProductTags: () => Promise.resolve(),
});

const { Provider } = shopifyContext;

const ShopifyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const showMessage = useNotification();
  const [checkoutInitialized, setCheckoutInitialized] = useState<boolean>(false);
  const [checkoutBusy, setCheckoutBusy] = useState<boolean>(false);
  const [checkoutState, setCheckoutState] = useState<CGCart>();
  const [lastCheckoutState, setLastCheckoutState] = useState<CGCart>();

  useEffect(() => {
    const loadCheckout = async () => {
      const currentCheckoutId = await localStorage.getItem('currentCheckoutId');
      d('gotCheckoutId', currentCheckoutId);
      // const id = 'Z2lkOi8vc2hvcGlmeS9DaGVja291dC82MWZlMDE5ZThlMTk4YjBjOWU3ZmVjZmI5NDBkYTEwZj9rZXk9ZmU1Nzg1YzkyNTA4ZGQxOWFiYzc2MWY4ZDc1MDJmZTk=';
      if (currentCheckoutId) {
        setCheckoutBusy(true);
        const checkout = await shopifyClient.checkout.fetch(currentCheckoutId);
        // Check if the order has been completed or timedout
        if (checkout === null || (checkout && checkout.completedAt)) {
          setLastCheckoutState(checkout);
          d('fetchedCompletedCheckout', checkout);
          localStorage.removeItem('currentCheckoutId');
          // TODO - Redirect to thank you page?
        } else {
          setCheckoutState(checkout);
          d('fetchedCheckout', checkout);
          localStorage.setItem('currentCheckoutId', checkout.id.toString());
        }
        setCheckoutBusy(false);
        setCheckoutInitialized(true);
      } else {
        setCheckoutInitialized(true);
      }
    };
    loadCheckout();
  }, []);

  const getOrCreateCheckout = async (): Promise<Cart> => {
    if (checkoutState) return checkoutState;
    const checkout = await shopifyClient.checkout.create();
    localStorage.setItem('currentCheckoutId', checkout.id.toString());
    return checkout;
  };

  const addLineItems = async (lineItemsToAdd: CGLineItem[]): Promise<any> => {
    setCheckoutBusy(true);
    const currentCart = await getOrCreateCheckout();
    const nextCart = await shopifyClient.checkout.addLineItems(currentCart.id, lineItemsToAdd);

    // BEGIN analytics...
    const originalLineItemIds = currentCart.lineItems.map((lineItem) => lineItem.id);
    const newLineItems = nextCart.lineItems.filter((lineItem) => originalLineItemIds.indexOf(lineItem.id) === -1);
    // Calculate the total value of newly added items...
    const totalOfNewLineItems: number = newLineItems.reduce((prevValue, lineItem) => {
      return prevValue + Number(get(lineItem, 'variant.priceV2.amount', 99999)) * lineItem.quantity;
    }, 0);
    // Send off the add_to_cart event...
    cgtag('add_to_cart', {
      ecommerce: {
        currency: get(currentCart, 'currencyCode', 'AUD'),
        value: totalOfNewLineItems,
        items: newLineItems.map((lineItem) => shopifyLineItemToEcommerceLineItem(lineItem)),
      },
    });
    // END analytics...

    setCheckoutState(nextCart);
    setCheckoutBusy(false);
    return nextCart;
  };

  const addGiftCards = async (code: string): Promise<any> => {
    const currentCart = await getOrCreateCheckout();
    setCheckoutBusy(true);
    // @ts-ignore
    return shopifyClient.checkout.addGiftCards(currentCart.id, [code]).then((response) => {
      setCheckoutState(response);
      setCheckoutBusy(false);
      return response;
    });
  };

  const applyGiftCard = (code: string) => {
    d('ApplyCode', code);
    addGiftCards(code)
      .then((x) => {
        if (x.userErrors && x.userErrors.length) {
          showMessage(x.userErrors[0].message, 'error');
          return;
        }
        if (x.errors === undefined) {
          showMessage('Gift Card Applied!', 'success');
        } else {
          showMessage('Error applying card...', 'error');
        }
      })
      .catch((err) => {
        showMessage('Error applying card: ' + err.message, 'error');
      });
  };

  const removeGiftCard = async (code: string): Promise<any> => {
    const currentCart = await getOrCreateCheckout();
    setCheckoutBusy(true);
    // @ts-ignore
    return shopifyClient.checkout.removeGiftCard(currentCart.id, code).then((response) => {
      d('removeGiftCard', code);
      setCheckoutState(response);
      setCheckoutBusy(false);
      return response;
    });
  };

  const removeLineItem = async (lineItemId: string): Promise<CGCart> => {
    setCheckoutBusy(true);
    const currentCart = await getOrCreateCheckout();

    // BEGIN analytics...
    const lineItem = currentCart.lineItems.find((lineItem) => lineItem.id === lineItemId);
    if (lineItem) {
      const totalOfRemovedLineItem: number = Number(get(lineItem, 'variant.priceV2.amount', 99999)) * lineItem.quantity;
      cgtag('remove_from_cart', {
        ecommerce: {
          currency: get(currentCart, 'currencyCode', 'AUD'),
          value: totalOfRemovedLineItem,
          items: [shopifyLineItemToEcommerceLineItem(lineItem)],
        },
      });
    }
    // END analytics...

    const nextCart: CGCart = await shopifyClient.checkout.removeLineItems(currentCart.id, [lineItemId]);
    setCheckoutState(nextCart);
    setCheckoutBusy(false);
    return nextCart;
  };

  const updateLineItem = async (lineItemId: string, quantity: number, cardGiftData: string): Promise<CGCart> => {
    setCheckoutBusy(true);
    const currentCart = await getOrCreateCheckout();
    const nextCart: CGCart = await shopifyClient.checkout.updateLineItems(currentCart.id, [
      {
        id: lineItemId,
        quantity,
        // @ts-ignore
        customAttributes: [
          {
            key: '_card.gift',
            value: cardGiftData,
          },
        ],
      },
    ]);
    setCheckoutState(nextCart);
    setCheckoutBusy(false);
    return nextCart;
  };

  return (
    <Provider
      value={{
        checkoutInitialized,
        checkoutBusy,
        checkoutState,
        lastCheckoutState,
        applyGiftCard,
        addLineItems,
        removeLineItem,
        updateLineItem,
        addGiftCards,
        removeGiftCard,
        predictiveSearch: getShopifyProxySiteSearchResults,
        getProductTags: getShopifyProxyProductTags,
      }}
    >
      {children}
    </Provider>
  );
};

export default ShopifyProvider;
